import React from 'react';
import './index.scss';

interface FieldLabelProps {
  children: React.ReactNode;
  /** Adds the asterisk. The field itself still enforces it. */
  required?: boolean;
  disabled?: boolean;
  /** Ties the label to its control for screen readers and click-to-focus. */
  htmlFor?: string;
  /**
   * For controls that are not a single labelable element. MUI's Rating renders a group of radios
   * inside a span, so `htmlFor` has nothing to point at; the caller instead names a wrapping
   * `role="group"` with `aria-labelledby` and passes that id here.
   */
  id?: string;
}

/**
 * The label above a form field.
 *
 * One component because the spec was written three times — FormField and
 * SelectFormField both defined `.form-field-label`, AutocompleteFormField
 * defined `.autocomplete-form-field-label` saying the same thing, and the
 * disabled state had drifted to two different selectors. PhoneInput had none
 * of them and passed its label to MUI instead, which draws it inside the
 * notched outline: in a form of four fields, one of them labelled itself
 * differently from the other three.
 */
const FieldLabel: React.FC<FieldLabelProps> = ({
  children,
  required = false,
  disabled = false,
  htmlFor,
  id,
}) => (
  <label id={id} className={`cc-field-label${disabled ? ' is-disabled' : ''}`} htmlFor={htmlFor}>
    {children}
    {required && <span className="cc-field-label__required">*</span>}
  </label>
);

export default FieldLabel;
